import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader, Panel, Chip, Button, Modal, FormRow, Textarea, Select, EmptyState, SegmentedControl, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post } from '../lib/api'
import { money, dateShort, ago } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { disputeLabel } from './client/disputes'

type Dispute = {
  id: string; dispute_status: 'open' | 'approved' | 'denied'; dispute_reason: string; dispute_category: string | null; has_image: boolean; billed_cents: number | null; disputed_at: string; dispute_note: string | null
  dispute_resolved_at: string | null; list_cents: number | null; prepaid_credit_cents: number; overage_cents: number; billed_at: string
  post_id: string; client_id: string; client_name: string; tier_name: string | null; summary: string | null; group_name: string | null
  rep_name: string; resolved_by_name: string | null
  billing_cycle?: 'monthly' | 'weekly'; claimed_at?: string | null; tier_lowered_from?: string | null
  lower_tiers?: { id: string; name: string }[] | null
}

export default function Disputes({ clientId, embedded = false }: { clientId?: string; embedded?: boolean } = {}) {
  const [params, setParams] = useSearchParams()
  const [localStatus, setLocalStatus] = useState(clientId ? 'all' : 'open')
  const status = embedded ? localStatus : (params.get('status') || 'open')
  const setStatus = (v: string) => (embedded ? setLocalStatus(v) : setParams({ status: v }, { replace: true }))
  const q = new URLSearchParams()
  if (status !== 'all') q.set('status', status)
  if (clientId) q.set('client', clientId)
  const { data, error, reload } = useApi<{ disputes: Dispute[] }>(`/api/disputes${q.toString() ? `?${q}` : ''}`)
  const [acting, setActing] = useState<{ d: Dispute; approve: boolean } | null>(null)

  return (
    <>
      {embedded
        ? <div className="panel-header"><div><h2 className="panel-title">Disputes</h2><p className="muted" style={{ margin: 0 }}>Leads this client asked to get their money back on. Approving puts the money back exactly as it left.</p></div></div>
        : <PageHeader title="Disputes" subtitle="Leads a client asked to get their money back on. Approving puts the money back exactly as it left: credit to their balance, and off their auto-buy invoice." />}
      <div style={{ marginBottom: 'var(--s-6)' }}>
        <SegmentedControl value={status} onChange={setStatus}
          segments={[{ value: 'open', label: 'Open' }, { value: 'approved', label: 'Refunded' }, { value: 'denied', label: 'Kept' }, { value: 'all', label: 'All' }]} />
      </div>
      {error && <ErrorBlock error={error} />}
      {!data ? <LoadingBlock /> : data.disputes.length === 0 ? (
        <Panel><EmptyState title={status === 'open' ? 'No open disputes' : 'Nothing here'} body="When a client disputes a lead from their portal it shows up here, and admins get a notice." /></Panel>
      ) : (
        <div className="stack">
          {data.disputes.map((d) => (
            <Panel key={d.id}>
              <div className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div className="stack-tight" style={{ gap: 'var(--s-2)' }}>
                  <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    {clientId ? <strong>{d.client_name}</strong> : <Link to={`/clients/${d.client_id}`}><strong>{d.client_name}</strong></Link>}
                    {d.tier_name && <Chip tone="accent">{d.tier_name}</Chip>}
                    <Chip tone={d.dispute_status === 'open' ? 'warn' : d.dispute_status === 'approved' ? 'pos' : undefined} dot>
                      {d.dispute_status === 'open' ? 'Open' : d.dispute_status === 'approved' ? 'Refunded' : 'Kept'}
                    </Chip>
                  </div>
                  <Link to={`/leads/${d.post_id}`}>{d.summary || 'A lead'}</Link>
                  <span className="muted">{d.group_name || 'a group'} · carried by {d.rep_name} · delivered {dateShort(d.billed_at)} · disputed {ago(d.disputed_at)}</span>
                </div>
                <div style={{ textAlign: 'right', marginLeft: 'auto' }}>
                  <strong>{money(d.list_cents ?? d.billed_cents)}</strong>
                  <div className="muted nowrap">{d.prepaid_credit_cents ? `${money(d.prepaid_credit_cents)} from balance` : ''}{d.prepaid_credit_cents && d.overage_cents ? ', ' : ''}{d.overage_cents ? `${money(d.overage_cents)} auto-buy` : ''}</div>
                </div>
              </div>
              <div className="dispute-claim">
                <div>
                  <span className="hero-kicker">{disputeLabel(d.dispute_category)}</span>
                  <blockquote className="lead-post" style={{ margin: 'var(--s-2) 0 0' }}>{d.dispute_reason}</blockquote>
                </div>
                {d.has_image && (
                  <a href={`/api/disputes/${d.id}/image`} target="_blank" rel="noopener noreferrer" className="dispute-claim-shot" aria-label="Open the client's screenshot">
                    <img src={`/api/disputes/${d.id}/image`} alt="The client's screenshot" loading="lazy" />
                  </a>
                )}
              </div>
              {d.dispute_status === 'open' ? (
                <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                  <Button onClick={() => setActing({ d, approve: true })}>{d.billing_cycle === 'weekly' ? 'It holds' : 'Refund it'}</Button>
                  <Button variant="secondary" onClick={() => setActing({ d, approve: false })}>{d.billing_cycle === 'weekly' ? 'It does not hold' : 'Keep the charge'}</Button>
                </div>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  {d.dispute_status === 'approved' ? (d.tier_lowered_from ? `Tier lowered from ${d.tier_lowered_from} to ${d.tier_name}` : d.billing_cycle === 'weekly' ? 'Held: not billed' : 'Refunded') : 'Kept'}{d.resolved_by_name ? ` by ${d.resolved_by_name}` : ''} {ago(d.dispute_resolved_at)}{d.dispute_note ? `: ${d.dispute_note}` : ''}
                </p>
              )}
            </Panel>
          ))}
        </div>
      )}
      {acting && <Decide {...acting} onClose={() => setActing(null)} onDone={reload} />}
    </>
  )
}

function Decide({ d, approve, onClose, onDone }: { d: Dispute; approve: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [note, setNote] = useState('')
  const [lowerTo, setLowerTo] = useState('')
  const [busy, setBusy] = useState(false)
  const weekly = d.billing_cycle === 'weekly'
  const go = async () => {
    if (!approve && !note.trim()) return toast('Tell the client why in the note', 'neg')
    setBusy(true)
    try {
      await post(`/api/disputes/${d.id}/${approve ? 'approve' : 'deny'}`, { note: note || undefined, tier_id: approve && lowerTo ? lowerTo : undefined })
      toast(!approve ? (weekly ? 'Kept: it goes on the next invoice' : 'Charge kept') : lowerTo ? 'Tier lowered' : weekly ? 'Held: it will not be billed' : 'Refunded', 'pos'); onDone(); onClose()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Modal title={weekly ? (approve ? `The dispute from ${d.client_name} holds` : `The dispute from ${d.client_name} does not hold`) : approve ? `Refund ${d.client_name}?` : `Keep the charge to ${d.client_name}?`} onClose={onClose}
           footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={busy}>{weekly ? (approve ? (lowerTo ? 'Lower the tier' : 'Do not bill it') : 'Keep it') : approve ? 'Refund' : 'Keep the charge'}</Button></>}>
      <div className="stack-tight">
        <p style={{ margin: 0 }}>{weekly
          ? (approve ? 'The lead is not invoiced at all, or, if the tier was too high, it is billed at the lower tier you choose (Business Terms 7.3).'
            : 'The lead stays claimed and goes on their next weekly invoice. The client sees your note.')
          : approve
            ? `${d.prepaid_credit_cents ? `${money(d.prepaid_credit_cents)} of credit goes back on their balance. ` : ''}${d.overage_cents ? `${money(d.overage_cents)} comes off their auto-buy invoice. ` : ''}The lead stays on record as refunded.`
            : 'The client sees your note on the lead.'}</p>
        {weekly && approve && (d.lower_tiers?.length ?? 0) > 0 && (
          <FormRow label="What holds" htmlFor="dz-tier">
            <Select id="dz-tier" value={lowerTo} onChange={(e) => setLowerTo(e.target.value)}>
              <option value="">Do not bill this lead at all</option>
              {d.lower_tiers!.map((t) => <option key={t.id} value={t.id}>Lower the tier to {t.name}</option>)}
            </Select>
          </FormRow>
        )}
        <FormRow label={approve ? 'Note to the client (optional)' : 'Why (the client sees this)'} htmlFor="dz-note">
          <Textarea id="dz-note" rows={3} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </FormRow>
      </div>
    </Modal>
  )
}
